import {FC} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {Alert, Stack} from "@mui/material";
import {useBulkUnenrollUserFromCourses} from "@/app2/shared/data";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";

interface BulkUnenrollModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	userId: number;
	courseIds: number[];
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

/**
 * Modal for bulk unenrolling a user from courses
 */
export const BulkUnenrollModal: FC<BulkUnenrollModalProps> = ({
	open,
	onClose,
	onSuccess,
	userId,
	courseIds
}) => {
	const {t} = useSSTranslation();
	const {mutate: bulkUnenroll, isPending, error, reset} = useBulkUnenrollUserFromCourses(userId);

	const handleConfirm = () => {
		bulkUnenroll(courseIds, {
			onSuccess: (result) => {
				if (result.errors.length === 0) {
					onSuccess();
				}
			}
		});
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason !== "backdropClick") {
					handleClose();
				}
			}}
			maxWidth="sm"
			fullWidth
			aria-labelledby="bulk-unenroll-dialog-title"
			aria-describedby="bulk-unenroll-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="bulk-unenroll-dialog-title">Unenroll from Courses</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<DialogContentText id="bulk-unenroll-dialog-description" variant="body1" color="text.primary">
						Are you sure you want to unenroll this user from {courseIds.length} course
						{courseIds.length !== 1 ? "s" : ""}?
					</DialogContentText>
					<DialogContentText variant="body2" color="text.primary">
						This will remove all course progress and cannot be undone.
					</DialogContentText>
					{error && (
						<Alert severity="error">
							{error.message}
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={handleClose} sx={buttonSx} disabled={isPending}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={isPending}
					startIcon={isPending ? <CircularProgress size={16} /> : undefined}
				>
					{isPending ? t("common_unenrolling", "Unenrolling...") : t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
